import { EApplicantType } from '@if/core/types'
import { Banner, EnumBanner, SubsectionTitle } from '@if/mui'
import { Stack } from '@mui/material'
import { ApplicantDto, CreditAlertsDto } from '@src/types/CreditApplicationSchema'
import { useTranslation } from 'react-i18next'
import { ECreditApplicationStatusMessage } from '../../viewCreditApp-selectors'

interface Props {
  applicant: ApplicantDto
  coApplicant?: ApplicantDto | null
  // The app-level credit status (same for both applicants); drives each banner's color/label.
  status: ECreditApplicationStatusMessage | null
  // Whether to list credit alerts (only programs requiring credit-step intervention do).
  showAlerts: boolean
}

// Credit alert flags shown per applicant, with their i18n message key.
const CREDIT_ALERTS = [
  { flag: 'hasNameMismatch', key: 'viewCreditApplication.nameMismatch' },
  { flag: 'hasDoBMismatch', key: 'viewCreditApplication.dobMismatch' },
  { flag: 'hasCreditLocked', key: 'viewCreditApplication.creditLocked' },
  { flag: 'hasInvalidCity', key: 'viewCreditApplication.invalidCity' },
  { flag: 'hasGeneralCreditReportError', key: 'viewCreditApplication.generalCreditReportError' },
  { flag: 'isBanned', key: 'viewCreditApplication.bannedApplicant' },
] as const satisfies readonly { flag: keyof CreditAlertsDto; key: string }[]

// One block per applicant: name heading, then the credit-status banner (warning + alerts if any).
const CreditApplicantBanners = ({ applicant, coApplicant, status, showAlerts }: Props) => {
  const { t } = useTranslation()

  const applicantBlock = (person: ApplicantDto) => {
    const applicantType = person.isPrimaryApplicant ? EApplicantType.Applicant : EApplicantType.CoApplicant
    const alerts = showAlerts ? CREDIT_ALERTS.filter((alert) => person.creditAlerts?.[alert.flag]) : []
    return (
      <Stack spacing={1}>
        <SubsectionTitle>{`${person.firstName} ${person.lastName} (${t(`common.${applicantType}`)})`}</SubsectionTitle>
        {status !== null ? (
          <EnumBanner set={ECreditApplicationStatusMessage} value={status}>
            {alerts.length > 0 ? alerts.map((alert) => t(alert.key)).join(' · ') : undefined}
          </EnumBanner>
        ) : (
          <Banner severity={alerts.length > 0 ? 'warning' : 'success'}>
            {alerts.length > 0
              ? alerts.map((alert) => t(alert.key)).join(' · ')
              : t('viewCreditApplication.creditNoAlerts')}
          </Banner>
        )}
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      {applicantBlock(applicant)}
      {coApplicant && applicantBlock(coApplicant)}
    </Stack>
  )
}

export default CreditApplicantBanners
